/*
  Kiwi Ears · accessory guides (Rene 2026-08-30) — sections/ag-*.

  Four small behaviours for the Cables and Fit & Ear Tips guides, ported from
  the Buying-Guide package's interactive articles:

    1. CONNECTION FINDER — source x priority resolves to one of twelve written
       answers. The answer texts are the package's, verbatim: they are
       editorial, they name a product only where one actually solves that path,
       and three of the twelve name none. They live here rather than in section
       settings because they are a MATRIX — twelve cells that must all exist
       for any pair of choices — and a matrix maintained as loose customizer
       fields is a matrix with holes in it.
    2. FIT CHECK — four yes/no signs resolve to one prioritised next step.
       The priority order is the package's and is the whole point: comfort
       outranks seal outranks stability. Questions start UNANSWERED (see the
       note above setupFitCheck), and a reported problem resolves without
       waiting for the rest.
    3. MATERIAL TABS — real tablist semantics over server-rendered panels.
    4. QUICK-ADD on the guides' product cards, scoped to ag-products (the
       wishlist's own handler is scoped to the wishlist page, so a band that
       does not bind one ships dead cart buttons).

  Loaded by several sections, so everything is behind one run-once guard: a
  <script src> executes once per TAG, and three of these bands can be on one
  page. Every setup returns early when its band is absent.

  No motion here, so nothing to gate on prefers-reduced-motion; the guides'
  only animation is the hero's drawn path, which is CSS and gated there.
*/
(function () {
  'use strict';
  if (window.__kiwiGuide) return;
  window.__kiwiGuide = true;

  var qs = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var qsa = function (sel, ctx) { return [].slice.call((ctx || document).querySelectorAll(sel)); };

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* ------------------------------------------------ 1 · connection finder */
  var CABLE = {
    'usbc-simple': {
      title: 'Keep your existing cable. Add a USB-C DAC adapter.',
      copy: 'The simplest path is a compact USB-C-to-3.5mm DAC adapter. Your current IEM cable stays in place, and the adapter handles the digital connection to the phone, tablet, or computer.',
      path: ['USB-C device', 'DAC adapter', '3.5mm cable', 'IEM'],
      points: ['Check that the device supports USB Audio.', 'Choose an adapter with the controls or microphone support you need.', 'Kiwi Ears AD1 is the relevant product reference.']
    },
    'usbc-modular': {
      title: 'Use one modular cable across USB-C, 3.5mm, and 4.4mm.',
      copy: 'A modular source-side cable is the cleanest option when you move between a phone, laptop, DAP, and desktop system. Swap the plug module instead of replacing the full cable.',
      path: ['USB-C / 3.5mm / 4.4mm', 'modular plug', '0.78mm 2-pin', 'IEM'],
      points: ['Verify the IEM-side connector before buying.', 'Choose the shortest practical length for portable use.', 'Kiwi Ears Terras is the relevant product reference.']
    },
    'usbc-mic': {
      title: 'Choose a USB-C IEM cable with microphone and controls.',
      copy: 'For calls, mobile gaming, and chat, the cable needs more than audio conductors. Look for an integrated DAC, compatible controls, and the microphone format that matches how you play or communicate.',
      path: ['USB-C device', 'DAC + controls', 'inline / boom mic', 'IEM'],
      points: ['Confirm device and app support for USB audio and microphone input.', 'Keep controls within easy reach without adding cable weight.', 'Kiwi Ears S-Link Type-C is the relevant product reference.']
    },
    '35-simple': {
      title: 'Use a standard 3.5mm cable and keep the path simple.',
      copy: 'A well-fitting 3.5mm cable is the most universal wired option for devices that still include a headphone output. Prioritize secure connectors, low handling noise, useful length, and a comfortable ear hook.',
      path: ['3.5mm source', 'IEM cable', '0.78mm 2-pin', 'IEM'],
      points: ['No adapter is needed when the source already has 3.5mm output.', 'A shorter cable reduces clutter for portable use.', 'Do not assume a different plug will automatically improve sound.']
    },
    '35-modular': {
      title: 'Start with 3.5mm, but keep future sources open.',
      copy: 'A modular cable lets you use 3.5mm today and change to USB-C or 4.4mm later. It is most useful when the same IEM travels between portable and desktop systems.',
      path: ['3.5mm now', 'modular source end', '0.78mm 2-pin', 'IEM'],
      points: ['Store unused modules in a case.', 'Swap modules while disconnected from the source.', 'Kiwi Ears Terras is the relevant product reference.']
    },
    '35-mic': {
      title: 'Choose an inline-microphone cable that matches your device wiring.',
      copy: 'For a 3.5mm headset connection, confirm the source accepts a four-pole TRRS plug and that the microphone/control wiring is compatible. A normal three-pole audio cable will not carry microphone input.',
      path: ['3.5mm TRRS source', 'inline mic / controls', 'IEM'],
      points: ['Check CTIA compatibility on the device.', 'Desktop PCs may require a headset splitter.', 'Test call controls before relying on them for work or gaming.']
    },
    '44-simple': {
      title: 'Use 4.4mm only with a source designed for it.',
      copy: 'A 4.4mm cable is appropriate when your DAP, DAC, or amplifier has a compatible balanced output. It is a connection choice — not an automatic sound-quality upgrade.',
      path: ['4.4mm balanced output', '4.4mm cable', '0.78mm 2-pin', 'IEM'],
      points: ['Never force a 4.4mm plug into another socket.', 'Start at low volume because balanced outputs may provide more level.', 'Confirm the source manual and IEM connector type.']
    },
    '44-modular': {
      title: 'Use a modular cable for balanced desktop and portable listening.',
      copy: 'Choose a cable with a dedicated 4.4mm module when you want one IEM cable to move between a balanced source and more common 3.5mm or USB-C devices.',
      path: ['4.4mm / 3.5mm / USB-C', 'modular plug', '0.78mm 2-pin', 'IEM'],
      points: ['Turn the source off before changing modules.', 'Match volume when comparing outputs.', 'Kiwi Ears Terras is the relevant product reference.']
    },
    '44-mic': {
      title: 'Separate balanced listening from microphone use.',
      copy: 'Most 4.4mm balanced outputs are built for audio only and do not carry a headset microphone. Use a dedicated USB-C or 3.5mm microphone cable when communication is required.',
      path: ['4.4mm for listening', 'USB-C / TRRS for chat', 'IEM'],
      points: ['Do not assume a balanced port accepts microphone input.', 'Keep a second communication cable or use a modular system.', 'Choose the connection for the task, not the label.']
    },
    'gaming-simple': {
      title: 'Use a direct USB-C gaming cable for the fewest parts.',
      copy: 'A direct digital cable reduces adapters and keeps controls, microphone input, and game modes in one reachable place. It is the most straightforward mobile-gaming route.',
      path: ['Phone / tablet', 'USB-C gaming cable', 'microphone', 'IEM'],
      points: ['Confirm the game recognizes the selected microphone.', 'Use the cable cinch to reduce movement.', 'Kiwi Ears S-Link Type-C is the relevant product reference.']
    },
    'gaming-modular': {
      title: 'Use a modular listening cable plus a separate chat solution.',
      copy: 'This route works when music quality and source flexibility matter more than a built-in microphone. Pair a modular cable with the device or external microphone system used for communication.',
      path: ['Modular cable', 'chosen source', 'separate microphone', 'IEM'],
      points: ['Keep microphone routing independent from the IEM cable.', 'Avoid stacking loose adapters during movement.', 'Kiwi Ears Terras can serve as the listening cable reference.']
    },
    'gaming-mic': {
      title: 'Prioritize microphone placement, controls, and low handling noise.',
      copy: 'The best gaming cable should make mute, volume, mode switching, and voice pickup easy without pulling on the IEM. A detachable boom microphone adds focus when team communication matters.',
      path: ['USB-C gaming device', 'smart controls', 'boom / inline mic', 'IEM'],
      points: ['Place the boom outside breath airflow.', 'Route the cable so controls do not strike clothing.', 'Kiwi Ears S-Link Type-C is the relevant product reference.']
    }
  };

  function setupFinder() {
    var shell = qs('[data-ag-finder]');
    if (!shell) return;
    var source = 'usbc', priority = 'simple';
    var sourceBtns = qsa('[data-ag-source]', shell);
    var prioBtns = qsa('[data-ag-priority]', shell);
    var title = qs('[data-ag-rtitle]', shell);
    var copy = qs('[data-ag-rcopy]', shell);
    var path = qs('[data-ag-rpath]', shell);
    var list = qs('[data-ag-rlist]', shell);
    if (!title || !copy || !path || !list) return;

    function paint() {
      var r = CABLE[source + '-' + priority] || CABLE['usbc-simple'];
      title.textContent = r.title;
      copy.textContent = r.copy;
      path.textContent = '';
      r.path.forEach(function (step, i) {
        if (i) path.appendChild(el('li', 'ag-pathsep', '→'));
        path.appendChild(el('li', 'ag-pathpill', step));
      });
      list.textContent = '';
      r.points.forEach(function (p) { list.appendChild(el('li', 'ag-check', p)); });
    }
    function bind(btns, attr, set) {
      btns.forEach(function (b) {
        b.addEventListener('click', function () {
          set(b.getAttribute(attr));
          btns.forEach(function (o) { o.setAttribute('aria-pressed', String(o === b)); });
          paint();
        });
      });
    }
    bind(sourceBtns, 'data-ag-source', function (v) { source = v; });
    bind(prioBtns, 'data-ag-priority', function (v) { priority = v; });
    paint();
  }

  /* -------------------------------------------------------- 2 · fit check */
  /*
    Four signs -> one prioritised next step.

    Two rules carry the whole behaviour:

    PRIORITY, not arithmetic. Comfort outranks seal outranks stability. A
    painful fit is a failed fit whatever else is true, and an averaged score
    would happily call it "3 out of 4, nearly there" and send someone back to
    an IEM that hurts.

    A "No" RESOLVES IMMEDIATELY, before the remaining questions are answered,
    because the priority rule only needs the No. Making someone answer three
    more questions before being told to take the IEM out would be worse than
    useless. Everything-Yes has to wait for all four, since "no problem found"
    is only true once there is nothing left to find.
  */
  function setupFitCheck() {
    var shell = qs('[data-ag-fitcheck]');
    if (!shell) return;
    var score = qs('[data-ag-score]', shell);
    var title = qs('[data-ag-ftitle]', shell);
    var copy = qs('[data-ag-fcopy]', shell);
    var list = qs('[data-ag-flist]', shell);
    if (!score || !title || !copy || !list) return;

    var rows = qsa('[data-ag-q]', shell);
    if (!rows.length) return;
    /* null = not answered yet. The check must never report on an answer
       nobody gave, which is what defaulting every question to Yes did. */
    var answers = {};
    rows.forEach(function (r) { answers[r.getAttribute('data-ag-q')] = null; });

    var WAIT_TITLE = title.textContent;
    var WAIT_COPY = copy.textContent;

    var VERDICTS = {
      comfort: {
        title: 'Comfort needs to be fixed before anything else.',
        copy: 'Pressure, soreness, or a hot spot is not a successful fit — even when bass and isolation are strong. Try a smaller, softer, or differently shaped tip and reduce insertion depth.',
        actions: ['Stop using the fit if it causes pain or persistent pressure.', 'Try one size smaller or a softer flange.', 'Check whether the shell — not the tip — is creating the hot spot.']
      },
      seal: {
        title: 'The seal is probably incomplete or uneven.',
        copy: 'Weak bass and a drifting center image are common signs that one or both tips are not sealing consistently. Change one variable at a time.',
        actions: ['Try the next size up, then the next size down.', 'Reseat each side with the same insertion depth.', 'Inspect the tip for folding and the nozzle for blockage.']
      },
      movement: {
        title: 'The seal works, but stability needs help.',
        copy: 'If the sound changes when you smile, speak, or walk, the tip or cable routing may not be supporting the shell consistently.',
        actions: ['Try a firmer flange or a slightly longer stem.', 'Route the cable over the ear and use the chin slider.', 'Check whether a smaller shell contact point improves movement.']
      },
      clear: {
        title: 'Your fit looks consistent.',
        copy: 'All four signs point toward a stable seal. Keep using the same tip, insertion depth, and cable routing so comparisons remain repeatable.',
        actions: ['Confirm the tip remains firmly attached to the nozzle.', 'Recheck comfort during a longer listening session.', 'Keep listening volume moderate.']
      }
    };

    function paintScore(answered, total, yes, complete) {
      score.textContent = '';
      if (complete) {
        score.appendChild(document.createTextNode(String(yes)));
        score.appendChild(el('small', null, '/' + total));
        score.classList.remove('is-dots');
        return;
      }
      /* progress, not a verdict: one dot per question, filled as it is
         answered — a number here would read as a result */
      score.classList.add('is-dots');
      rows.forEach(function (r, i) {
        score.appendChild(el('span', 'ag-dot' + (i < answered ? ' is-on' : '')));
      });
    }

    function paint() {
      var total = rows.length;
      var answered = 0, yes = 0;
      rows.forEach(function (r) {
        var v = answers[r.getAttribute('data-ag-q')];
        if (v === null) return;
        answered++;
        if (v) yes++;
        r.classList.add('is-answered');
      });
      var complete = answered === total;

      var v;
      if (answers.comfort === false) v = VERDICTS.comfort;
      else if (answers.bass === false || answers.center === false) v = VERDICTS.seal;
      else if (answers.movement === false) v = VERDICTS.movement;
      else if (complete) v = VERDICTS.clear;

      paintScore(answered, total, yes, complete);
      list.textContent = '';

      if (!v) {
        /* nothing decided yet — say what is still needed, never a verdict */
        title.textContent = answered === 0 ? WAIT_TITLE : 'So far, every sign checks out.';
        copy.textContent = answered === 0
          ? WAIT_COPY
          : 'Nothing here points to a fit problem yet. Answer the remaining ' + (total - answered) + (total - answered === 1 ? ' sign' : ' signs') + ' to finish the check.';
        shell.setAttribute('data-ag-state', 'waiting');
        return;
      }
      title.textContent = v.title;
      copy.textContent = v.copy;
      v.actions.forEach(function (a) { list.appendChild(el('li', 'ag-check', a)); });
      shell.setAttribute('data-ag-state', v === VERDICTS.comfort ? 'stop' : v === VERDICTS.clear ? 'good' : 'adjust');
    }

    rows.forEach(function (row) {
      var key = row.getAttribute('data-ag-q');
      var btns = qsa('[role="radio"]', row);
      function choose(b, focus) {
        answers[key] = b.getAttribute('data-answer') === 'yes';
        btns.forEach(function (o) {
          var on = o === b;
          o.setAttribute('aria-checked', String(on));
          /* roving tabindex: the group is one tab stop, arrows move within it
             — the radiogroup pattern this markup promises */
          o.tabIndex = on ? 0 : -1;
        });
        if (focus) b.focus();
        paint();
      }
      btns.forEach(function (b, i) {
        b.addEventListener('click', function () { choose(b); });
        b.addEventListener('keydown', function (e) {
          var n = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1 : -1;
          if (n === -1) return;
          e.preventDefault();
          choose(btns[(n + btns.length) % btns.length], true);
        });
      });
    });
    paint();
  }

  /* ---------------------------------------------------- 3 · material tabs */
  function setupTabs() {
    qsa('[data-ag-tabs]').forEach(function (shell) {
      var tabs = qsa('[role="tab"]', shell);
      var panels = qsa('[role="tabpanel"]', shell);
      if (tabs.length < 2 || !panels.length) return;

      function select(i, focus) {
        tabs.forEach(function (t, n) {
          var on = n === i;
          t.setAttribute('aria-selected', String(on));
          t.tabIndex = on ? 0 : -1;
          if (on && focus) t.focus();
        });
        panels.forEach(function (p, n) {
          /* `hidden` rather than a class: it also removes the panel's links
             from the tab order, which display:none-by-class would too but
             visually-hidden alternatives would not. */
          p.hidden = n !== i;
        });
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { select(i); });
        t.addEventListener('keydown', function (e) {
          var n = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? i + 1
                : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? i - 1
                : e.key === 'Home' ? 0
                : e.key === 'End' ? tabs.length - 1 : -1;
          if (n === -1) return;
          e.preventDefault();
          select((n + tabs.length) % tabs.length, true);
        });
      });
      /* Only now do the extra panels go away — before this the band is four
         readable comparisons, which is what a no-JS visitor keeps. */
      shell.classList.add('is-ready');
      select(0);
    });
  }

  /* Quick add on .ag-products is handled by assets/kiwi-quickadd.js
     (Rene 2026-09-04) — one delegated .pk-padd handler for the whole site.
     Keeping this copy would have added every guide product twice. */

  function arm() {
    setupFinder();
    setupFitCheck();
    setupTabs();
  }
  if (document.readyState !== 'loading') arm();
  else document.addEventListener('DOMContentLoaded', arm);
})();
